<script setup lang="ts">
import type { LocationQueryRaw } from "vue-router"
import type { Album } from "@/models"
import cover from "@brand-cover"
import { useUrlSearchParams } from "@vueuse/core"
import { onMounted, ref, useTemplateRef, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { AlbumSelectorTitle } from "@/components"
import { emitSelectedAlbum } from "@/components/Nav/AlbumIdEventBus"
import { Icon, Tooltip } from "@/components/ui"
import { useTranslation } from "@/composables/useTranslation"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { AlbumIdEventBus } from "../Nav/AlbumIdEventBus"

interface AlbumSelectorProps {
  albums?: Album[]
  eventCoverImage?: string
  modelValue?: number
  hideMainGallery?: boolean
  hideGuestbook?: boolean
  isLoading?: boolean
}

const props = withDefaults(defineProps<AlbumSelectorProps>(), {
  albums: () => [],
  eventCoverImage: "",
  hideMainGallery: false,
  isLoading: false,
})

const emit = defineEmits<{
  "update:modelValue": [value?: number]
  "select": [album: Album]
}>()

const { t } = useTranslation()
const router = useRouter()

const createdAlbumsContainer = useTemplateRef<HTMLDivElement | null>("createdAlbumsContainer")

const eventVaultStore = useEventVaultStore()

const revealedAlbumId = ref<number>()

const params = useUrlSearchParams<{ album?: unknown }>()

onMounted(() => {
  if (params.album && typeof params.album === "string") {
    const albumId = Number(params.album)
    emit("update:modelValue", albumId)
    eventVaultStore.setAlbumId(albumId)
  }
})

const hasScrolledOnLoad = ref<boolean>(false)

watchEffect(() => {
  if (hasScrolledOnLoad.value)
    return

  const selectedAlbum = createdAlbumsContainer.value?.querySelector(`[data-id="album-${props.modelValue}"]`)
  if (selectedAlbum) {
    selectedAlbum.scrollIntoView({ behavior: "auto", block: "center" })
    hasScrolledOnLoad.value = true
  }
})

function onTitleOpenChange(open: boolean, albumId?: number) {
  revealedAlbumId.value = open ? albumId : undefined
}

function onAlbumClick(album: Album) {
  revealedAlbumId.value = undefined

  if (album.isLocked && !eventVaultStore.isHost) {
    emit("select", album)
    return
  }

  if (album.id) {
    params.album = album.id.toString()
  }

  emit("update:modelValue", album.id)
  emit("select", album)
  emitSelectedAlbum(album.id)
  eventVaultStore.setAlbumId(album.id)
}

function removeAlbumParam() {
  delete params.album
  router.replace({ query: params as LocationQueryRaw })
  AlbumIdEventBus.emit(undefined)
  eventVaultStore.setAlbumId(undefined)
}

function onAlbumClear() {
  revealedAlbumId.value = undefined
  removeAlbumParam()
  emit("update:modelValue", undefined)
  eventVaultStore.setAlbumId(undefined)
}
</script>

<template>
  <div v-if="albums?.length" tabindex="-1" class="bg-gray-200 border-y border-gray-300">
    <div class="container py-3 md:py-4">
      <div>
        <div class="mb-1 flex w-full items-center gap-2">
          <Icon icon="Folder" />
          <h2 class="text-xl">
            {{ t("albums") }}
          </h2>
        </div>
        <p class="mb-2 text-sm text-gray-600 hidden md:block">
          {{ t(hideGuestbook ? "album_selector.select_album_description_no_guestbook" : "album_selector.select_album_description") }}
        </p>
      </div>
      <div class="flex flex-row gap-3 overflow-x-auto md:gap-5">
        <div v-if="!hideMainGallery" class="rounded-xl bg-secondary">
          <button
            data-test="gpx-album-selector-clear"
            type="button"
            class="m-0 max-w-min w-full flex-[1_1_auto] cursor-pointer overflow-hidden rounded-xl border-none p-0 transition-all duration-150 ease-in-out"
            :class="!modelValue ? 'bg-primary' : 'bg-secondary'"
            @click="onAlbumClear"
          >
            <span
              class="relative flex aspect-square w-[148px] min-w-[148px] items-center justify-center overflow-hidden border-none transition-all duration-150 ease-in-out md:aspect-video md:w-[280px] md:min-w-[280px]"
              :class="!modelValue ? 'grayscale-0' : 'grayscale'"
            >
              <span v-show="!modelValue" class="absolute inset-0 z-10 flex h-full items-center justify-center gap-2 place-content-center text-base transition-all duration-150 ease-in-out md:px-6 md:py-1">
                <span class="rounded-sm bg-white px-4 py-1 font-montserrat text-xs md:text-base font-normal text-primary">
                  {{ t("album_selector.selected") }}
                </span>
              </span>
              <img
                v-if="eventCoverImage"
                :src="eventCoverImage ?? cover"
                :alt="t('album_selector.main_cover_image_alt')"
                class="h-full w-full object-cover text-[0]"
              >
              <img
                v-else :src="cover"
                :alt="t('album_selector.main_cover_image_alt')"
                class="h-full w-full object-cover text-[0]"
              >
            </span>
            <span
              class="flex items-center gap-1.5 p-1.5 text-start font-montserrat text-sm font-normal [&_svg]:shrink-0 [&_svg]:self-start"
              :class="!modelValue ? 'text-primary-card-text' : 'text-secondary-card-text'"
            >
              <Icon icon="Folder" />{{ t("album_selector.main_gallery") }}
            </span>
          </button>
        </div>
        <div>
          <transition mode="out-in" name="gpx-fade">
            <div v-if="isLoading" class="flex flex-row gap-3 md:gap-4">
              <div
                v-for="n in 5"
                :key="`skeleton-${n}`"
                class="m-0 max-w-min w-full flex-1 cursor-pointer overflow-hidden rounded-xl border-none bg-secondary p-0 transition-all duration-150 ease-in-out"
              >
                <div class="gpx-skeleton relative flex aspect-square w-[148px] min-w-[148px] items-center justify-center overflow-hidden border-none grayscale transition-all duration-150 ease-in-out md:aspect-video md:w-[280px] md:min-w-[280px]" />
              </div>
            </div>
            <div v-else ref="createdAlbumsContainer" class="flex flex-row gap-3 md:gap-4">
              <button
                v-for="(album, index) in albums" :key="`album-${index}`" :data-id="`album-${album.id}`"
                type="button"
                class="m-0 max-w-min w-full flex-1 cursor-pointer overflow-hidden rounded-xl border-none p-0 transition-all duration-150 ease-in-out"
                :class="modelValue === album.id ? 'bg-primary' : 'bg-secondary'"
                @click.exact="() => onAlbumClick(album)"
              >
                <span
                  class="relative flex aspect-square w-[148px] min-w-[148px] items-center justify-center overflow-hidden border-none transition-all duration-150 ease-in-out md:aspect-video md:w-[280px] md:min-w-[280px]"
                  :class="modelValue === album.id ? 'grayscale-0' : 'grayscale'"
                >
                  <span
                    v-show="modelValue === album.id"
                    class="absolute inset-0 flex h-full items-center justify-center gap-2 place-content-center text-base transition-all duration-150 ease-in-out md:px-6 md:py-1"
                  >
                    <span class="rounded-sm bg-white px-4 py-1 font-montserrat text-xs md:text-base font-normal text-primary">
                      {{ t("album_selector.selected") }}
                    </span>
                  </span>
                  <img
                    v-if="album.coverImageUrl" :src="album.coverImageUrl"
                    :alt="t('album_selector.cover_image_alt')"
                    class="h-full w-full object-cover text-[0]"
                  >
                </span>
                <span>
                  <span
                    class="flex items-center gap-1.5 p-1.5 text-start font-montserrat text-sm font-normal [&_svg]:shrink-0 [&_svg]:self-start"
                    :class="modelValue === album.id ? 'text-primary-card-text' : 'text-secondary-card-text'"
                  >
                    <Tooltip v-if="album.isLocked">
                      <template #content>
                        {{ t('album_selector.locked_tooltip') }}
                      </template>
                      <Icon icon="Lock" />
                    </Tooltip>
                    <Tooltip v-else-if="album.isHidden">
                      <template #content>
                        {{ t('album_selector.hidden_tooltip') }}
                      </template>
                      <Icon icon="EyeOff" />
                    </Tooltip>
                    <Icon v-else icon="Folder" />
                    <AlbumSelectorTitle
                      :title="album.title ?? ''"
                      :popover-open="revealedAlbumId === album.id"
                      @update:popover-open="open => onTitleOpenChange(open, album.id)"
                    />
                  </span>
                </span>
              </button>
            </div>
          </transition>
        </div>
      </div>
      <p class="mt-2 md:hidden flex items-center gap-1.5 text-xs text-gray-600 justify-end">
        {{ t("album_selector.scroll_right_to_browse_albums") }}
        <Icon icon="ArrowRight" class="size-4 shrink-0" />
      </p>
    </div>
  </div>
</template>
